import { InteractiveStraightLine, InteractiveText } from '@/components/Shapes/InteractiveShapes/Wrapper';
import { FilledArea } from '@/components/Shapes/InteractiveShapes/Wrapper/FilledArea';
import type { FontOption } from '@/types/properties';
import React from 'react';

export const renderXABCDDottedLines = (
  points: [number, number][],
  strokeStyle: string,
  strokeWidth: number,
  fontApperance: FontOption,
  fillStyle: string,
  idPrefix: string = '',
  type: 'LINE' | 'XLINE' | 'RAY' | 'BOUND' = 'LINE',
) => {
  if (!points || points.length < 2) return null;

  const connections: [number, number][] = [
    [0, 2], // X to B
    [1, 3], // A to C
    [2, 4], // B to D
    [0, 4], // X to D
  ];

  const computeLabelValue = (index: number): string => {
    try {
      const [X, A, B, C, D] = points;
      switch (index) {
        case 0: // X-B
          return (Math.abs(A[1] - B[1]) / Math.abs(X[1] - A[1])).toFixed(3);

        case 1: // A-C
          return (Math.abs(B[1] - C[1]) / Math.abs(A[1] - B[1])).toFixed(3);

        case 2: // B-D
          return (Math.abs(C[1] - D[1]) / Math.abs(B[1] - C[1])).toFixed(3);

        case 3: // X-D
          return (Math.abs(A[1] - D[1]) / Math.abs(X[1] - A[1])).toFixed(3);

        default:
          return '';
      }
    } catch {
      return '';
    }
  };

  return connections
    .filter(([startIdx, endIdx]) => points[startIdx] && points[endIdx])
    .map(([startIdx, endIdx], index) => {
      const start = points[startIdx];
      const end = points[endIdx];
      const value = computeLabelValue(index);
      const midX = (start[0] + end[0]) / 2;
      const midY = (start[1] + end[1]) / 2;
      const { fill, fontFamily, fontSize, fontStyle, fontWeight } = fontApperance;
      return (
        <React.Fragment key={`xabcd-connection-${startIdx}-${endIdx}-${idPrefix}`}>
          <InteractiveStraightLine
            key={`line-${startIdx}-${endIdx}-${idPrefix}`}
            x1Value={start[0]}
            y1Value={start[1]}
            x2Value={end[0]}
            y2Value={end[1]}
            strokeStyle={strokeStyle}
            strokeWidth={1}
            strokeDasharray="ShortDot"
            // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
            type={type as 'XLINE' | 'RAY' | 'LINE'}
            selected={true}
          />

          <InteractiveText
            key={`label-${startIdx}-${endIdx}-${idPrefix}`}
            selected={true}
            position={[midX, midY]}
            bgFillStyle={strokeStyle}
            bgStroke={strokeStyle}
            bgStrokeWidth={0}
            textFill={fill}
            fontFamily={fontFamily}
            fontStyle={fontStyle}
            fontWeight={fontWeight}
            fontSize={fontSize}
            text={value}
            showBackground={true}
            paddingX={6}
            paddingY={4}
          />

          {points.length >= 3 && (
            <FilledArea
              key={`fill-area-0-1-2-${startIdx}-${endIdx}-${idPrefix}`}
              x1Value={points[0][0]}
              y1Value={points[0][1]}
              x2Value={points[1][0]}
              y2Value={points[1][1]}
              x3Value={points[2][0]}
              y3Value={points[2][1]}
              selected={false}
              strokeStyle="rgba(0, 0, 0, 0)"
              strokeWidth={0}
              strokeDasharray="ShortDot"
              fillStyle={fillStyle}
              type="LINE"
            />
          )}

          {points.length >= 5 && (
            <FilledArea
              key={`fill-area-2-3-4-${startIdx}-${endIdx}-${idPrefix}`}
              x1Value={points[2][0]}
              y1Value={points[2][1]}
              x2Value={points[3][0]}
              y2Value={points[3][1]}
              x3Value={points[4][0]}
              y3Value={points[4][1]}
              selected={false}
              strokeStyle="rgba(0, 0, 0, 0)"
              strokeWidth={0}
              strokeDasharray="ShortDot"
              fillStyle={fillStyle}
              type="LINE"
            />
          )}
        </React.Fragment>
      );
    });
};
